"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mascot } from "@/components/Mascot";
import { useMetadata } from "@/hooks/useMetadata";
import { getPageMessages, cycleMessages } from "@/lib/loadingMessages";

export type LoadingPageProps = {
  /** Metadata key (must exist in lib/metadata). Defaults to "loading". */
  metaKey?: string;
  /** Small_uppercase_eyebrow, e.g. "Loading". */
  kicker?: string;
  /** Big display headline. */
  title?: string;
  /** Optional route hint to pick the right message bank. */
  routeHint?: string;
  /** Whether to show the cycling route-aware message line. */
  showMessages?: boolean;
  /** Whether to auto-redirect after a timeout (ms). 0 = never. */
  redirectAfter?: number;
  /** Where to redirect. */
  redirectTo?: string;
  /** Maximum time (ms) before showing a fallback message. 0 = no timeout. */
  timeoutAfter?: number;
  /** Callback when the timeout fires. */
  onTimeout?: () => void;
};

const CYCLE_MS = 2_000;

/**
 * Shared full-screen, standalone loading surface. No site nav/header — just
 * the brand grid, a waiting mascot, a gold progress bar, and (optionally)
 * the same route-aware message bank used across the app's segment loaders.
 * Keeps every loading state on one visual language with StatusPage.
 */
export default function LoadingPage({
  metaKey = "loading",
  kicker = "Loading",
  title = "Hold Tight.",
  routeHint = "/",
  showMessages = true,
  redirectAfter = 0,
  redirectTo,
  timeoutAfter = 0,
  onTimeout,
}: LoadingPageProps) {
  useMetadata({ key: metaKey });
  const [messages, setMessages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setMessages(getPageMessages(routeHint));
    setIndex(0);
  }, [routeHint]);

  useEffect(() => {
    if (messages.length === 0) return;
    const interval = setInterval(() => setIndex((prev) => prev + 1), CYCLE_MS);
    return () => clearInterval(interval);
  }, [messages]);

  useEffect(() => {
    if (!redirectAfter || !redirectTo) return;
    const t = setTimeout(() => {
      window.location.href = redirectTo;
    }, redirectAfter);
    return () => clearTimeout(t);
  }, [redirectAfter, redirectTo]);

  useEffect(() => {
    if (!timeoutAfter || timedOut) return;
    const t = setTimeout(() => {
      setTimedOut(true);
      onTimeout?.();
    }, timeoutAfter);
    return () => clearTimeout(t);
  }, [timeoutAfter, timedOut, onTimeout]);

  const message = showMessages
    ? (cycleMessages(messages, index) ?? "Loading…")
    : null;

  if (timedOut) {
    return (
      <div
        className="min-h-screen bg-background text-foreground overflow-hidden relative flex flex-col"
        role="status"
        aria-live="polite"
        aria-label="Loading"
      >
        {/* Background grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg,transparent,transparent_59px,rgba(255,214,0,0.04) 59px,rgba(255,214,0,0.04) 60px),repeating-linear-gradient(0deg,transparent,transparent_59px,rgba(255,214,0,0.04) 59px,rgba(255,214,0,0.04) 60px)",
          }}
        />
        {/* Scanline */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            background:
              "linear-gradient(to bottom,transparent 50%,rgba(255,214,0,0.012) 50%)",
            backgroundSize: "100% 4px",
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md space-y-8 text-center"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-y/30 bg-y/5">
              <span className="w-1.5 h-1.5 rounded-full bg-y animate-pulse" />
              <span className="text-xs font-black uppercase tracking-[0.3em] text-y">
                Still loading
              </span>
            </div>

            <div className="flex justify-center">
              <Mascot
                state="waiting"
                size="large"
                className="animate-fade-in animate-mascot-idle"
              />
            </div>

            <div className="space-y-2">
              <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight leading-none">
                {title}
              </h1>
              <p className="text-sm font-black uppercase tracking-[0.2em] text-muted-foreground">
                The connection is taking longer than expected. Please wait…
              </p>
            </div>

            <div className="h-[3px] w-full max-w-xs mx-auto rounded-full bg-border overflow-hidden">
              <div className="h-full bg-y w-1/3 indeterminate-bar" />
            </div>
          </motion.div>
        </div>

        <div className="relative z-10 border-t border-border px-6 py-4">
          <p className="text-center text-xs font-black uppercase tracking-[0.3em] text-muted-foreground">
            UniUI — Academic Intelligence Platform
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground overflow-hidden relative flex flex-col"
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg,transparent,transparent_59px,rgba(255,214,0,0.04) 59px,rgba(255,214,0,0.04) 60px),repeating-linear-gradient(0deg,transparent,transparent_59px,rgba(255,214,0,0.04) 59px,rgba(255,214,0,0.04) 60px)",
        }}
      />
      {/* Scanline */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            "linear-gradient(to bottom,transparent 50%,rgba(255,214,0,0.012) 50%)",
          backgroundSize: "100% 4px",
        }}
      />

      {/* Top progress bar */}
      <div
        className="absolute top-0 left-0 h-[2px] bg-y progress-bar-loader z-20"
        aria-hidden="true"
      />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md space-y-8 text-center"
        >
          {/* Status pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-y/30 bg-y/5">
            <span className="w-1.5 h-1.5 rounded-full bg-y animate-pulse" />
            <span className="text-xs font-black uppercase tracking-[0.3em] text-y">
              {kicker}
            </span>
          </div>

          <div className="flex justify-center">
            <Mascot
              state="waiting"
              size="large"
              className="animate-fade-in animate-mascot-idle"
            />
          </div>

          <div className="space-y-2">
            <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight leading-none">
              {title}
            </h1>
            <p
              key={message ?? "static"}
              className="text-sm font-black uppercase tracking-[0.2em] text-muted-foreground min-h-[1.5em] msg-fade"
            >
              {message ?? "Preparing your experience…"}
            </p>
          </div>

          {/* Explicit determinate bar for the standalone page */}
          <div className="h-[3px] w-full max-w-xs mx-auto rounded-full bg-border overflow-hidden">
            <div className="h-full bg-y w-1/3 indeterminate-bar" />
          </div>
        </motion.div>
      </div>

      <div className="relative z-10 border-t border-border px-6 py-4">
        <p className="text-center text-xs font-black uppercase tracking-[0.3em] text-muted-foreground">
          UniUI — Academic Intelligence Platform
        </p>
      </div>
    </div>
  );
}
